import { createContext, PropsWithChildren, useContext } from "react";
import { useLayout } from "./LayoutContext";
import { MIDDLEWARE_ENDPOINT } from "../theme/configurationVariables";

export interface RequestContextProps {
    sendRequest: <T>(config: SendRequestModel) => Promise<DashboardResponseModel<T>>;
}

export const RequestContext = createContext<RequestContextProps | null>(null);

export const RequestProvider = ({ children }: PropsWithChildren) => {
    const { handleOpenLoader, handleCloseLoader, handleSuccessAlert, handleErrorAlert } = useLayout();

    const sendRequest = async function <T>(config: SendRequestModel) {
        const { path, alertOnSuccess, body, formData, hideAlertOnError, hideLoader, method, queryParams, loaderText } = config;

        if (!hideLoader) handleOpenLoader(loaderText);

        const headers = new Headers();
        if (body) headers.append("content-type", "application/json");

        const queryParamsValue = queryParams
            ? "?" +
              Object.keys(queryParams)
                  .map((x) => `${x}=${queryParams[x]}`)
                  .join("&")
            : "";

        try {
            const apiResponse = await fetch(MIDDLEWARE_ENDPOINT + path + queryParamsValue, {
                method: !method ? "get" : method,
                body: body ? JSON.stringify(body) : formData,
                headers,
            });

            if (!hideLoader) handleCloseLoader();

            const response = (await apiResponse.json()) as DashboardResponseModel<T>;

            if (response.hasError && !hideAlertOnError) handleErrorAlert(response.message);

            if (!response.hasError && alertOnSuccess) handleSuccessAlert(response.message);

            return response;
        } catch (error) {
            console.error(error);

            if (!hideLoader) handleCloseLoader();

            return { hasError: true, message: "Error en envío de solicitud de recurso" } as DashboardResponseModel<T>;
        }
    };

    return (
        <RequestContext.Provider
            value={{
                sendRequest,
            }}
        >
            {children}
        </RequestContext.Provider>
    );
};

export const useRequest = () => useContext(RequestContext) as RequestContextProps;

export interface SendRequestModel {
    path: string;
    body?: any;
    queryParams?: Record<string, string>;
    method?: string;
    formData?: FormData;
    loaderText?: string;
    alertOnSuccess?: boolean;
    hideAlertOnError?: boolean;
    hideLoader?: boolean;
}

export interface DashboardResponseModel<T> {
    hasError: boolean;
    message: string;
    data: T;
}
